// ════════════════════════════════════════════════════════════════════════
// DNACore V1 LEGACY ONLY — Do not add new features here.
// Esta tela é legada e está apenas aguardando descontinuação. Toda nova
// funcionalidade de "Aluno / Cadastro / Editar / Novo" deve ser feita em
// /app/frontend/src/pages/DNACore2/Aluno360 (V2). A rota V1 /students é
// redirecionada para /v2/aluno em App.js — este arquivo só é carregado
// caso seja referenciado diretamente.
// ════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { studentsAPI, responsiblesAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, UserPlus, Phone, Mail, MapPin, Edit, MessageCircle, GraduationCap, FileText, Building, Heart, DollarSign, Utensils, Languages, Download, Upload, CreditCard, User, Users, X, ArrowRightLeft, History, Calendar, ArrowRight } from 'lucide-react';
import { canEdit } from '@/lib/auth';
import { formatCPF, formatPhone } from '@/lib/utils';

const PLAN_CATEGORIES = {
  mensalidade: { label: 'Mensalidade Escolar', icon: GraduationCap, color: 'bg-blue-100 text-blue-700' },
  material: { label: 'Material Didático', icon: FileText, color: 'bg-purple-100 text-purple-700' },
  matricula: { label: 'Taxa de Matrícula', icon: DollarSign, color: 'bg-green-100 text-green-700' },
  bilingue: { label: 'Programa Bilíngue', icon: Languages, color: 'bg-orange-100 text-orange-700' },
  integral_almoco: { label: 'Período Integral c/ Almoço', icon: Utensils, color: 'bg-red-100 text-red-700' },
  integral_sem_almoco: { label: 'Período Integral s/ Almoço', icon: Utensils, color: 'bg-amber-100 text-amber-700' }
};

export default function Students() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [coursePlans, setCoursePlans] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [respDialogOpen, setRespDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingResponsible, setEditingResponsible] = useState(null);
  const [newResponsibleFromStudent, setNewResponsibleFromStudent] = useState(false);
  const [activeTab, setActiveTab] = useState('dados'); // Tab ativa no modal
  
  // Estados para importação
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Planos financeiros selecionados para o aluno
  const [selectedPlans, setSelectedPlans] = useState({});
  const [selectedClassGroup, setSelectedClassGroup] = useState(null);
  
  // Estado para edição de plano financeiro
  const [editingPlan, setEditingPlan] = useState(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planFormData, setPlanFormData] = useState({
    name: '',
    num_installments: 12,
    installment_value: 0,
    due_day: 10,
    start_date: '',
    discount_percent: 0,
    discount_days_before: 3,
    discount_enabled: false
  });
  
  // Estado para transferência de turma
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferringStudent, setTransferringStudent] = useState(null);
  const [transferFormData, setTransferFormData] = useState({
    new_class_group_id: '',
    new_call_number: '',
    keep_grades: true,
    keep_attendance: true
  });
  const [transferLoading, setTransferLoading] = useState(false);
  
  // Estado para histórico de transferências
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyStudent, setHistoryStudent] = useState(null);
  const [transferHistory, setTransferHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Estado para parcelas mês a mês
  const [parcelasAno, setParcelasAno] = useState(new Date().getFullYear());
  const [parcelas, setParcelas] = useState([]);
  const [parcelasLoading, setParcelasLoading] = useState(false);
  const [studentFinancialPlan, setStudentFinancialPlan] = useState(null);
  
  // Estados para busca de responsáveis
  const [searchRespFinanceiro, setSearchRespFinanceiro] = useState('');
  const [searchRespOpcional, setSearchRespOpcional] = useState('');
  const [searchRespDidatico, setSearchRespDidatico] = useState('');
  const [showRespFinanceiroList, setShowRespFinanceiroList] = useState(false);
  const [showRespOpcionalList, setShowRespOpcionalList] = useState(false);
  const [showRespDidaticoList, setShowRespDidaticoList] = useState(false);
  
  // Filtrar responsáveis por busca (nome ou CPF)
  const filterResponsibles = (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) return [];
    const term = searchTerm.toLowerCase().replace(/[.-]/g, '');
    return responsibles.filter(r => 
      r.name?.toLowerCase().includes(term) || 
      r.cpf?.replace(/[.-]/g, '').includes(term)
    ).slice(0, 10);
  };
  
  const meses = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ];
  
  // Estado completo do aluno baseado na planilha
  const [formData, setFormData] = useState({
    // Dados do Aluno
    name: '',
    cpf: '',
    rg: '',
    rg_orgao: '',
    rg_data_exp: '',
    registration_number: '',
    ra: '',
    class_name: '',
    class_group_id: '',  // ID da turma para matrícula
    year: new Date().getFullYear(),
    situation: 'Ativo',
    gender: '',
    birth_date: '',
    birth_city: '',
    nationality: 'Brasileiro(a)',
    is_foreigner: false,
    civil_status: 'Solteiro(a)',
    profession: '',
    workplace: '',
    classification: '',
    
    // Endereço e Comunicação
    zip_code: '',
    address: '',
    address_number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    phone: '',
    phone_commercial: '',
    cellphone: '',
    cellphone_carrier: '',
    whatsapp: '',
    email: '',
    no_email: false,
    no_sms: false,
    
    // Responsáveis
    responsible_id: '',
    responsible_financial_optional_id: '',
    responsible_didactic_id: '',
    
    // Dados de Saúde
    blood_type: '',
    allergies: '',
    diseases: '',
    first_aid_notes: '',
    skin_color: '',
    disabilities: '',
    
    // Outros
    authorized_pickup: '',
    authorized_pickup_phone: '',
    authorized_pickup_relation: '',
    permanence_entry: '',
    permanence_exit: '',
    notes: '',
    active: true
  });
  
  // Estado completo do responsável
  const [respFormData, setRespFormData] = useState({
    name: '',
    cpf: '',
    rg: '',
    rg_orgao: '',
    birth_date: '',
    nationality: 'Brasileiro(a)',
    profession: '',
    workplace: '',
    
    // Endereço
    zip_code: '',
    address: '',
    address_number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    
    // Contato
    phone: '',
    phone_commercial: '',
    cellphone: '',
    whatsapp: '',
    email: '',
    email_secondary: '',
    
    // Relação
    relation_type: 'Pai/Mãe',
    is_financial_responsible: true,
    
    notes: ''
  });

  const states = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const relationTypes = [
    'Pai/Mãe', 'Avô/Avó', 'Tio/Tia', 'Irmão/Irmã', 'Padrasto/Madrasta', 
    'Tutor Legal', 'Responsável Judicial', 'Outro'
  ];

  const genderOptions = ['Masculino', 'Feminino', 'Outro', 'Não Informado'];
  const civilStatusOptions = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'];
  const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Não Informado'];
  const skinColors = ['Branco', 'Pardo', 'Preto', 'Amarelo', 'Indígena', 'Não Declarada'];

  useEffect(() => {
    loadData();
  }, []);

  // Auto-open edit modal when ?edit=studentId is in URL
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && students.length > 0 && !editingStudent) {
      const found = students.find(s => s.id === editId);
      if (found) handleEditStudent(found);
    }
  }, [searchParams, students]);

  const loadData = async () => {
    try {
      const [studentsRes, responsiblesRes, classGroupsRes, coursesRes, plansRes, scholarshipsRes] = await Promise.all([
        studentsAPI.list(),
        responsiblesAPI.list(),
        api.get('/class-groups?year=2026'),
        api.get('/courses'),
        api.get('/course-financial-plans'),
        api.get('/scholarships')
      ]);
      setStudents(studentsRes.data);
      setResponsibles(responsiblesRes.data);
      setClassGroups(classGroupsRes.data);
      setCourses(coursesRes.data);
      setCoursePlans(plansRes.data);
      setScholarships(scholarshipsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Quando selecionar turma, buscar os planos do curso vinculado
  const handleClassChange = (classGroupId) => {
    const classGroup = classGroups.find(g => g.id === classGroupId);
    if (!classGroup) return;
    
    setFormData({ 
      ...formData, 
      class_name: classGroup.name,
      class_group_id: classGroup.id 
    });
    setSelectedClassGroup(classGroup);
    
    if (classGroup?.course_id) {
      // Buscar planos do curso e pré-selecionar os padrões
      const plans = coursePlans.filter(p => p.course_id === classGroup.course_id && p.active);
      const defaultSelected = {};
      plans.forEach(plan => {
        if (plan.is_default) {
          defaultSelected[plan.category] = plan.id;
        }
      });
      setSelectedPlans(defaultSelected);
    } else {
      setSelectedPlans({});
    }
  };

  const getAvailablePlansForCategory = (category) => {
    if (!selectedClassGroup?.course_id) return [];
    return coursePlans.filter(p => 
      p.course_id === selectedClassGroup.course_id && 
      p.category === category && 
      p.active
    );
  };

  // Funções para edição de plano financeiro
  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanFormData({
      name: plan.name || '',
      num_installments: plan.num_installments || 12,
      installment_value: plan.installment_value || 0,
      due_day: plan.due_day || 10,
      start_date: plan.start_date || '',
      discount_percent: plan.discount_percent || 50,
      discount_days_before: plan.discount_days_before || 3,
      discount_enabled: plan.discount_enabled || false
    });
    setPlanDialogOpen(true);
  };

  const handleSavePlan = async () => {
    try {
      await api.put(`/course-financial-plans/${editingPlan.id}`, {
        ...editingPlan,
        name: planFormData.name,
        num_installments: parseInt(planFormData.num_installments),
        installment_value: parseFloat(planFormData.installment_value),
        due_day: parseInt(planFormData.due_day),
        start_date: planFormData.start_date,
        discount_percent: parseFloat(planFormData.discount_percent),
        discount_days_before: parseInt(planFormData.discount_days_before),
        discount_enabled: planFormData.discount_enabled
      });
      toast.success('Plano atualizado com sucesso!');
      setPlanDialogOpen(false);
      setEditingPlan(null);
      // Recarregar planos
      const plansRes = await api.get('/course-financial-plans');
      setCoursePlans(plansRes.data);
    } catch (error) {
      toast.error('Erro ao atualizar plano');
    }
  };

  // Remover plano selecionado de uma categoria
  const handleRemovePlanFromCategory = (catKey) => {
    setSelectedPlans({
      ...selectedPlans,
      [catKey]: undefined
    });
    toast.success('Plano removido da seleção');
  };

  const resetStudentForm = () => {
    setFormData({
      name: '', cpf: '', rg: '', rg_orgao: '', rg_data_exp: '',
      registration_number: '', ra: '', class_name: '', class_group_id: '', year: new Date().getFullYear(),
      situation: 'Ativo', gender: '', birth_date: '', birth_city: '',
      nationality: 'Brasileiro(a)', is_foreigner: false, civil_status: 'Solteiro(a)',
      profession: '', workplace: '', classification: '',
      zip_code: '', address: '', address_number: '', complement: '',
      neighborhood: '', city: '', state: '', phone: '', phone_commercial: '',
      cellphone: '', cellphone_carrier: '', whatsapp: '', email: '',
      no_email: false, no_sms: false,
      responsible_id: '', responsible_financial_optional_id: '', responsible_didactic_id: '',
      blood_type: '', allergies: '', diseases: '', first_aid_notes: '',
      skin_color: '', disabilities: '',
      authorized_pickup: '', authorized_pickup_phone: '', authorized_pickup_relation: '',
      permanence_entry: '', permanence_exit: '', notes: '', active: true
    });
    setEditingStudent(null);
    setSelectedPlans({});
    setSelectedClassGroup(null);
    setActiveTab('dados'); // Reset para aba dados
    // Limpar campos de busca de responsáveis
    setSearchRespFinanceiro('');
    setSearchRespOpcional('');
    setSearchRespDidatico('');
  };

  const resetResponsibleForm = () => {
    setRespFormData({
      name: '', cpf: '', rg: '', rg_orgao: '', birth_date: '',
      nationality: 'Brasileiro(a)', profession: '', workplace: '',
      zip_code: '', address: '', address_number: '', complement: '',
      neighborhood: '', city: '', state: '',
      phone: '', phone_commercial: '', cellphone: '', whatsapp: '',
      email: '', email_secondary: '',
      relation_type: 'Pai/Mãe', is_financial_responsible: true, notes: ''
    });
    setEditingResponsible(null);
    setNewResponsibleFromStudent(false);
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    
    if (!formData.responsible_id) {
      toast.error('Selecione um responsável financeiro');
      return;
    }
    
    try {
      // Coletar IDs dos planos selecionados
      const selectedPlanIds = Object.values(selectedPlans).filter(id => id);
      
      const studentData = {
        ...formData,
        year: parseInt(formData.year),
        // Convert "none" back to empty string for optional responsible fields
        responsible_financial_optional_id: formData.responsible_financial_optional_id === 'none' ? '' : formData.responsible_financial_optional_id,
        responsible_didactic_id: formData.responsible_didactic_id === 'none' ? '' : formData.responsible_didactic_id,
        // Incluir planos financeiros selecionados
        financial_plans: selectedPlanIds
      };
      
      let studentId;
      if (editingStudent) {
        await studentsAPI.update(editingStudent.id, studentData);
        studentId = editingStudent.id;
        toast.success('Aluno atualizado com sucesso!');
        if (selectedPlanIds.length > 0) {
          toast.success(`Títulos serão gerados automaticamente para os novos planos!`);
        }
      } else {
        const response = await studentsAPI.create(studentData);
        studentId = response.data.id;
        toast.success('Aluno criado com sucesso!');
        if (selectedPlanIds.length > 0) {
          toast.success(`${selectedPlanIds.length} plano(s) financeiro(s) gerado(s)!`);
        }
      }
      setDialogOpen(false);
      resetStudentForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar aluno');
    }
  };

  const handleCreateResponsible = async (e) => {
    e.preventDefault();
    try {
      if (editingResponsible) {
        await responsiblesAPI.update(editingResponsible.id, respFormData);
        toast.success('Responsável atualizado com sucesso!');
      } else {
        const response = await responsiblesAPI.create(respFormData);
        toast.success('Responsável criado com sucesso!');
        
        // Se veio do cadastro de aluno, atualiza o responsável selecionado
        if (newResponsibleFromStudent) {
          setFormData({ ...formData, responsible_id: response.data.id });
        }
      }
      setRespDialogOpen(false);
      resetResponsibleForm();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar responsável');
    }
  };

  const handleEditStudent = (student) => {
    setEditingStudent(student);
    
    // Definir o selectedClassGroup para que os planos financeiros apareçam
    const classGroup = classGroups.find(g => g.id === student.class_group_id || g.name === student.class_name);
    setSelectedClassGroup(classGroup);
    
    // Carregar planos selecionados do aluno
    if (student.financial_plans) {
      const plansByCategory = {};
      student.financial_plans.forEach(planId => {
        const plan = coursePlans.find(p => p.id === planId);
        if (plan) {
          plansByCategory[plan.category] = planId;
        }
      });
      setSelectedPlans(plansByCategory);
    } else {
      setSelectedPlans({});
    }
    
    setFormData({
      name: student.name || '',
      cpf: student.cpf || '',
      rg: student.rg || '',
      rg_orgao: student.rg_orgao || '',
      rg_data_exp: student.rg_data_exp || '',
      registration_number: student.registration_number || '',
      ra: student.ra || '',
      class_name: student.class_name || '',
      class_group_id: student.class_group_id || classGroup?.id || '',  // ID da turma
      year: student.year || new Date().getFullYear(),
      situation: student.situation || 'Ativo',
      gender: student.gender || '',
      birth_date: student.birth_date || '',
      birth_city: student.birth_city || '',
      nationality: student.nationality || 'Brasileiro(a)',
      is_foreigner: student.is_foreigner || false,
      civil_status: student.civil_status || 'Solteiro(a)',
      profession: student.profession || '',
      workplace: student.workplace || '',
      classification: student.classification || '',
      zip_code: student.zip_code || '',
      address: student.address || '',
      address_number: student.address_number || '',
      complement: student.complement || '',
      neighborhood: student.neighborhood || '',
      city: student.city || '',
      state: student.state || '',
      phone: student.phone || '',
      phone_commercial: student.phone_commercial || '',
      cellphone: student.cellphone || '',
      cellphone_carrier: student.cellphone_carrier || '',
      whatsapp: student.whatsapp || '',
      email: student.email || '',
      no_email: student.no_email || false,
      no_sms: student.no_sms || false,
      responsible_id: student.responsible_id || '',
      responsible_financial_optional_id: student.responsible_financial_optional_id || '',
      responsible_didactic_id: student.responsible_didactic_id || '',
      blood_type: student.blood_type || '',
      allergies: student.allergies || '',
      diseases: student.diseases || '',
      first_aid_notes: student.first_aid_notes || '',
      skin_color: student.skin_color || '',
      disabilities: student.disabilities || '',
      authorized_pickup: student.authorized_pickup || '',
      authorized_pickup_phone: student.authorized_pickup_phone || '',
      authorized_pickup_relation: student.authorized_pickup_relation || '',
      permanence_entry: student.permanence_entry || '',
      permanence_exit: student.permanence_exit || '',
      notes: student.notes || '',
      active: student.active !== false
    });
    
    // Preencher campos de busca dos responsáveis ao editar
    if (student.responsible_id) {
      const resp = responsibles.find(r => r.id === student.responsible_id);
      if (resp) setSearchRespFinanceiro(`${resp.name} - ${formatCPF(resp.cpf)}`);
    } else {
      setSearchRespFinanceiro('');
    }
    if (student.responsible_financial_optional_id) {
      const resp = responsibles.find(r => r.id === student.responsible_financial_optional_id);
      if (resp) setSearchRespOpcional(`${resp.name} - ${formatCPF(resp.cpf)}`);
    } else {
      setSearchRespOpcional('');
    }
    if (student.responsible_didactic_id) {
      const resp = responsibles.find(r => r.id === student.responsible_didactic_id);
      if (resp) setSearchRespDidatico(`${resp.name} - ${formatCPF(resp.cpf)}`);
    } else {
      setSearchRespDidatico('');
    }
    
    setDialogOpen(true);
    
    // Carregar parcelas do aluno
    loadParcelasAluno(student.id, student.financial_plans);
  };

  const loadParcelasAluno = async (studentId, financialPlanIds = []) => {
    setParcelasLoading(true);
    try {
      // Buscar plano financeiro individual
      const planRes = await api.get(`/students/${studentId}/financial-plan`);
      setStudentFinancialPlan(planRes.data);
      
      // Coletar informações dos planos de curso selecionados
      let valorMensalTotal = 0;
      let descontoTotal = 0;
      let diaVencimento = planRes.data?.due_day || 10;
      let planosNomes = [];
      
      // Se tiver planos de curso selecionados, calcular o valor mensal e coletar nomes
      if (financialPlanIds && financialPlanIds.length > 0) {
        financialPlanIds.forEach(planId => {
          const plan = coursePlans.find(p => p.id === planId);
          if (plan) {
            valorMensalTotal += plan.installment_value || 0;
            planosNomes.push({
              id: plan.id,
              name: plan.name,
              category: plan.category,
              value: plan.installment_value || 0,
              discount_enabled: plan.discount_enabled,
              discount_percent: plan.discount_percent || 0
            });
            // Se o plano tiver desconto habilitado
            if (plan.discount_enabled && plan.discount_percent > 0) {
              descontoTotal += (plan.installment_value || 0) * (plan.discount_percent / 100);
            }
          }
        });
      } else if (planRes.data?.monthly_value > 0) {
        // Usar plano financeiro individual se não tiver planos de curso
        valorMensalTotal = planRes.data.monthly_value;
        descontoTotal = planRes.data.discount_value || (valorMensalTotal * (planRes.data.discount_percentage || 0) / 100);
        planosNomes.push({
          id: null,
          name: 'Mensalidade',
          category: 'mensalidade',
          value: valorMensalTotal
        });
      }
      
      // Criar descrição baseada nos planos
      const descricaoPlanos = planosNomes.length > 0 
        ? planosNomes.map(p => p.name).join(' + ')
        : 'Mensalidade';
      
      // Buscar títulos do aluno
      const res = await api.get(`/titles?student_id=${studentId}`);
      const titulos = res.data || [];
      
      const parcelasGeradas = meses.map(mes => {
        const referencia = `${parcelasAno}-${mes.value}`;
        const tituloExistente = titulos.find(t => 
          t.reference_month === referencia || 
          (t.due_date && t.due_date.startsWith(referencia))
        );
        
        if (tituloExistente) {
          // Usar paid_value quando pago (valor efetivamente pago)
          const valorPago = tituloExistente.status === 'pago' 
            ? (tituloExistente.paid_value || tituloExistente.original_value - (tituloExistente.discount_value || 0))
            : null;
          
          return {
            mes: mes.value,
            mesNome: mes.label,
            referencia,
            descricao: tituloExistente.description || descricaoPlanos,
            planos: planosNomes,
            valor_original: tituloExistente.original_value || 0,
            desconto: tituloExistente.discount_value || 0,
            valor_final: tituloExistente.original_value - (tituloExistente.discount_value || 0),
            valor_pago: valorPago,
            vencimento: tituloExistente.due_date || '',
            status: tituloExistente.status,
            titulo_id: tituloExistente.id,
            existente: true
          };
        } else {
          // Usar valores calculados dos planos de curso
          return {
            mes: mes.value,
            mesNome: mes.label,
            referencia,
            descricao: `${descricaoPlanos} - ${mes.label}/${parcelasAno}`,
            planos: planosNomes,
            valor_original: valorMensalTotal,
            desconto: descontoTotal,
            valor_final: valorMensalTotal - descontoTotal,
            valor_pago: null,
            vencimento: `${parcelasAno}-${mes.value}-${String(diaVencimento).padStart(2, '0')}`,
            status: null,
            titulo_id: null,
            existente: false
          };
        }
      });
      
      setParcelas(parcelasGeradas);
    } catch (error) {
      setStudentFinancialPlan(null);
      setParcelas([]);
    } finally {
      setParcelasLoading(false);
    }
  };

  const handleParcelaChange = (index, field, value) => {
    const novasParcelas = [...parcelas];
    novasParcelas[index] = { ...novasParcelas[index], [field]: value };
    
    if (field === 'valor_original' || field === 'desconto') {
      const original = field === 'valor_original' ? parseFloat(value) || 0 : parseFloat(novasParcelas[index].valor_original) || 0;
      const desc = field === 'desconto' ? parseFloat(value) || 0 : parseFloat(novasParcelas[index].desconto) || 0;
      novasParcelas[index].valor_final = original - desc;
    }
    
    setParcelas(novasParcelas);
  };

  const handleSaveParcelaIndividual = async (parcela, index) => {
    try {
      if (parcela.existente && parcela.titulo_id) {
        await api.put(`/titles/${parcela.titulo_id}`, {
          original_value: parseFloat(parcela.valor_original) || 0,
          discount_value: parseFloat(parcela.desconto) || 0,
          due_date: parcela.vencimento,
          description: parcela.descricao
        });
        toast.success(`Parcela de ${parcela.mesNome} atualizada!`);
      } else {
        await api.post('/titles', {
          student_id: editingStudent.id,
          reference_month: parcela.referencia,
          original_value: parseFloat(parcela.valor_original) || 0,
          discount_value: parseFloat(parcela.desconto) || 0,
          due_date: parcela.vencimento,
          description: parcela.descricao || `Mensalidade ${parcela.mesNome}/${parcelasAno}`
        });
        
        const novasParcelas = [...parcelas];
        novasParcelas[index].existente = true;
        novasParcelas[index].status = 'pendente';
        setParcelas(novasParcelas);
        
        toast.success(`Parcela de ${parcela.mesNome} criada!`);
      }
    } catch (error) {
      toast.error('Erro ao salvar parcela');
    }
  };

  const handleGerarTodasParcelas = async () => {
    if (!window.confirm(`Gerar/atualizar todas as parcelas de ${parcelasAno}?`)) return;
    
    let sucesso = 0;
    let erros = 0;
    
    for (let i = 0; i < parcelas.length; i++) {
      const parcela = parcelas[i];
      if (!parcela.valor_original || parcela.valor_original <= 0) continue;
      if (parcela.status === 'pago') continue;
      
      try {
        if (parcela.existente && parcela.titulo_id) {
          await api.put(`/titles/${parcela.titulo_id}`, {
            original_value: parseFloat(parcela.valor_original) || 0,
            discount_value: parseFloat(parcela.desconto) || 0,
            due_date: parcela.vencimento,
            description: parcela.descricao
          });
        } else {
          await api.post('/titles', {
            student_id: editingStudent.id,
            reference_month: parcela.referencia,
            original_value: parseFloat(parcela.valor_original) || 0,
            discount_value: parseFloat(parcela.desconto) || 0,
            due_date: parcela.vencimento,
            description: parcela.descricao || `Mensalidade ${parcela.mesNome}/${parcelasAno}`
          });
        }
        sucesso++;
      } catch (error) {
        erros++;
      }
    }
    
    if (sucesso > 0) {
      toast.success(`${sucesso} parcelas processadas!`);
      // Passar os planos selecionados ao recarregar
      const selectedPlanIds = Object.values(selectedPlans).filter(Boolean);
      loadParcelasAluno(editingStudent.id, selectedPlanIds.length > 0 ? selectedPlanIds : editingStudent.financial_plans);
    }
    if (erros > 0) {
      toast.error(`${erros} parcelas com erro`);
    }
  };

  const handleDeleteParcela = async (parcela, index) => {
    if (!parcela.titulo_id) return;
    if (parcela.status === 'pago') {
      toast.error('Não é possível excluir parcela já paga');
      return;
    }
    
    if (!window.confirm(`Excluir parcela de ${parcela.mesNome}?`)) return;
    
    try {
      await api.delete(`/titles/${parcela.titulo_id}`);
      
      const novasParcelas = [...parcelas];
      novasParcelas[index].existente = false;
      novasParcelas[index].titulo_id = null;
      novasParcelas[index].status = null;
      setParcelas(novasParcelas);
      
      toast.success('Parcela excluída!');
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleEditResponsible = (responsible) => {
    setEditingResponsible(responsible);
    setRespFormData({
      name: responsible.name || '',
      cpf: responsible.cpf || '',
      rg: responsible.rg || '',
      rg_orgao: responsible.rg_orgao || '',
      birth_date: responsible.birth_date || '',
      nationality: responsible.nationality || 'Brasileiro(a)',
      profession: responsible.profession || '',
      workplace: responsible.workplace || '',
      zip_code: responsible.zip_code || '',
      address: responsible.address || '',
      address_number: responsible.address_number || '',
      complement: responsible.complement || '',
      neighborhood: responsible.neighborhood || '',
      city: responsible.city || '',
      state: responsible.state || '',
      phone: responsible.phone || '',
      phone_commercial: responsible.phone_commercial || '',
      cellphone: responsible.cellphone || '',
      whatsapp: responsible.whatsapp || '',
      email: responsible.email || '',
      email_secondary: responsible.email_secondary || '',
      relation_type: responsible.relation_type || 'Pai/Mãe',
      is_financial_responsible: responsible.is_financial_responsible !== false,
      notes: responsible.notes || ''
    });
    setRespDialogOpen(true);
  };

  const openNewResponsibleFromStudent = () => {
    resetResponsibleForm();
    setNewResponsibleFromStudent(true);
    setRespDialogOpen(true);
  };

  // Função para baixar o template de importação
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_importacao_dnafusion.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  // Função para importar arquivo Excel
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
      return;
    }

    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = response.data;
      
      if (result.success) {
        toast.success(result.message || `Importação concluída! ${result.responsibles?.imported || 0} responsáveis e ${result.students?.imported || 0} alunos importados.`);
        
        // Mostrar erros se houver
        if (result.responsibles?.errors?.length > 0) {
          result.responsibles.errors.forEach(err => toast.warning(err));
        }
        if (result.students?.errors?.length > 0) {
          result.students.errors.forEach(err => toast.warning(err));
        }
        
        loadData();
      } else {
        toast.error(result.message || 'Erro na importação');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setImportLoading(false);
      setImportDialogOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Função para abrir WhatsApp
  const openWhatsApp = (phone, name) => {
    if (!phone) return;
    // Limpar o número (remover caracteres não numéricos)
    const cleanPhone = phone.replace(/\D/g, '');
    // Adicionar código do Brasil se não tiver
    const fullPhone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
    const message = encodeURIComponent(`Olá${name ? `, ${name.split(' ')[0]}` : ''}!`);
    window.open(`https://wa.me/${fullPhone}?text=${message}`, '_blank');
  };

  // Função para navegar ao plano financeiro do aluno
  const openFinancialPlan = (student) => {
    // Abrir o modal de edição do aluno na aba Financeiro
    setActiveTab('financeiro');
    handleEditStudent(student);
  };

  // Função para obter todos os responsáveis de um aluno (pedagógico e outros)
  const getStudentResponsibles = (student, financialResponsibleId) => {
    const otherResps = [];
    
    // Verificar responsável pedagógico/didático
    if (student.responsible_didactic_id && student.responsible_didactic_id !== financialResponsibleId) {
      const didactic = responsibles.find(r => r.id === student.responsible_didactic_id);
      if (didactic) {
        otherResps.push({ ...didactic, role: 'Responsável Pedagógico' });
      }
    }
    
    // Também verificar responsáveis que têm este aluno no student_ids
    const byStudentIds = responsibles.filter(r => 
      r.student_ids?.includes(student.id) && r.id !== financialResponsibleId
    );
    byStudentIds.forEach(r => {
      if (!otherResps.find(o => o.id === r.id)) {
        otherResps.push(r);
      }
    });
    
    return otherResps;
  };

  // Funções para transferência de turma
  const openTransferDialog = (student) => {
    setTransferringStudent(student);
    setTransferFormData({
      new_class_group_id: '',
      new_call_number: '',
      keep_grades: true,
      keep_attendance: true,
      reason: ''
    });
    setTransferDialogOpen(true);
  };

  const handleTransferStudent = async () => {
    if (!transferFormData.new_class_group_id) {
      toast.error('Selecione a turma de destino');
      return;
    }
    
    if (transferFormData.new_class_group_id === transferringStudent.class_group_id) {
      toast.error('Selecione uma turma diferente da atual');
      return;
    }
    
    setTransferLoading(true);
    try {
      const payload = {
        new_class_group_id: transferFormData.new_class_group_id,
        keep_grades: transferFormData.keep_grades,
        keep_attendance: transferFormData.keep_attendance
      };
      
      // Adicionar número de chamada se especificado
      if (transferFormData.new_call_number) {
        payload.new_call_number = parseInt(transferFormData.new_call_number);
      }
      
      // Adicionar motivo se especificado
      if (transferFormData.reason?.trim()) {
        payload.reason = transferFormData.reason.trim();
      }
      
      const response = await api.post(`/students/${transferringStudent.id}/transfer`, payload);
      
      toast.success(response.data.message);
      
      // Recarregar lista de alunos
      const res = await studentsAPI.getAll();
      setStudents(res.data);
      
      setTransferDialogOpen(false);
      setTransferringStudent(null);
    } catch (error) {
      console.error('Erro na transferência:', error);
      toast.error(error.response?.data?.detail || 'Erro ao transferir aluno');
    } finally {
      setTransferLoading(false);
    }
  };

  // Funções para histórico de transferências
  const openHistoryDialog = async (student) => {
    setHistoryStudent(student);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    
    try {
      const response = await api.get(`/students/${student.id}/transfer-history`);
      setTransferHistory(response.data.transfers || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico de transferências');
      setTransferHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatTransferDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.registration_number?.toLowerCase().includes(search.toLowerCase()) ||
    s.class_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="students-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Alunos</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">Gerenciar alunos e responsáveis</p>
        </div>
        {canEdit() && (
          <div className="flex flex-wrap gap-2">
            {/* Botão Download Template */}
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn" className="text-xs sm:text-sm">
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Template</span>
            </Button>
            
            {/* Botão Importar */}
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="import-btn" className="text-xs sm:text-sm">
                  <Upload className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Importar</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-lg sm:text-xl">Importar Dados via Excel</DialogTitle>
                  <DialogDescription className="text-sm">
                    Selecione um arquivo Excel com as abas "Responsaveis" e "Alunos" preenchidas conforme o template.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex flex-col gap-2">
                    <Label>Arquivo Excel (.xlsx)</Label>
                    <Input 
                      type="file" 
                      accept=".xlsx,.xls" 
                      onChange={handleImportExcel}
                      ref={fileInputRef}
                      disabled={importLoading}
                      data-testid="import-file-input"
                      className="text-sm"
                    />
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground space-y-2">
                    <p><strong>O template possui dados unificados em uma única aba:</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><span className="text-orange-600 font-medium">Dados do Aluno</span> - Nome*, Matrícula*, Turma*, etc.</li>
                      <li><span className="text-green-600 font-medium">Responsável Financeiro</span> - vinculado pelo CPF</li>
                      <li><span className="text-pink-600 font-medium">Mãe</span> - dados completos</li>
                      <li><span className="text-blue-600 font-medium">Pai</span> - dados completos</li>
                    </ul>
                    <p className="mt-2 text-[10px] sm:text-xs">
                      <strong>Vinculação automática:</strong> O aluno será vinculado à turma pelo nome e ao responsável financeiro pelo CPF.
                    </p>
                  </div>
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="w-full sm:w-auto">Cancelar</Button>
                  <Button onClick={handleDownloadTemplate} variant="secondary" className="w-full sm:w-auto">
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Template
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Botão Novo Responsável */}
            <Dialog open={respDialogOpen && !newResponsibleFromStudent} onOpenChange={(open) => {
              if (!newResponsibleFromStudent) {
                setRespDialogOpen(open);
                if (!open) resetResponsibleForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="new-responsible-button" size="sm" className="text-xs sm:text-sm">
                  <UserPlus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Novo Responsável</span>
                </Button>
              </DialogTrigger>
            </Dialog>

            {/* Botão Novo Aluno */}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetStudentForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="new-student-button" size="sm" className="text-xs sm:text-sm">
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Novo Aluno</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    <GraduationCap className="w-5 h-5" />
                    {editingStudent ? 'Editar Aluno' : 'Novo Aluno'}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    Preencha todas as informações do aluno
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateStudent} className="space-y-4">
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="w-full flex overflow-x-auto scrollbar-hide">
                      <TabsTrigger value="dados" className="flex-1 min-w-[70px] text-xs sm:text-sm px-2 sm:px-3">Dados</TabsTrigger>
                      <TabsTrigger value="endereco" className="flex-1 min-w-[70px] text-xs sm:text-sm px-2 sm:px-3">Endereço</TabsTrigger>
                      <TabsTrigger value="responsaveis" className="flex-1 min-w-[70px] text-xs sm:text-sm px-2 sm:px-3">Resp.</TabsTrigger>
                      <TabsTrigger value="saude" className="flex-1 min-w-[70px] text-xs sm:text-sm px-2 sm:px-3">Saúde</TabsTrigger>
                      <TabsTrigger value="financeiro" className="flex-1 min-w-[70px] text-xs sm:text-sm px-2 sm:px-3">Plano</TabsTrigger>
                      <TabsTrigger value="parcelas" className="flex-1 min-w-[70px] text-xs sm:text-sm px-2 sm:px-3">Parcelas</TabsTrigger>
                      <TabsTrigger value="outros" className="flex-1 min-w-[70px] text-xs sm:text-sm px-2 sm:px-3">Outros</TabsTrigger>
                    </TabsList>
                    
                    {/* ABA: Dados do Aluno */}
                    <TabsContent value="dados" className="space-y-4 mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                        <div className="sm:col-span-2">
                          <Label className="text-xs sm:text-sm">Nome Completo *</Label>
                          <Input 
                            value={formData.name} 
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                            required 
                            data-testid="student-name"
                            className="text-sm sm:text-base"
                          />
                        </div>
                        <div>
                          <Label className="text-xs sm:text-sm">Situação</Label>
                          <Select value={formData.situation} onValueChange={(v) => setFormData({ ...formData, situation: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Ativo">Ativo</SelectItem>
                              <SelectItem value="Inativo">Inativo</SelectItem>
                              <SelectItem value="Trancado">Trancado</SelectItem>
                              <SelectItem value="Transferido">Transferido</SelectItem>
                              <SelectItem value="Formado">Formado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                        <div>
                          <Label className="text-xs sm:text-sm">Nº Matrícula *</Label>
                          <Input value={formData.registration_number} onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })} required data-testid="student-registration" className="text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs sm:text-sm">RA</Label>
                          <Input value={formData.ra} onChange={(e) => setFormData({ ...formData, ra: e.target.value })} className="text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs sm:text-sm">Turma *</Label>
                          <Select value={formData.class_group_id || undefined} onValueChange={handleClassChange}>
                            <SelectTrigger data-testid="student-class">
                              <SelectValue placeholder="Selecione a turma" />
                            </SelectTrigger>
                            <SelectContent>
                              {classGroups
                                .filter(g => g.year === new Date().getFullYear() || g.year === formData.year)
                                .map(g => (
                                  <SelectItem key={g.id} value={g.id}>
                                    {g.code ? `${g.code} - ` : ''}{g.name} ({g.year})
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs sm:text-sm">Ano Letivo</Label>
                          <Input type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} className="text-sm" />
                        </div>
                      </div>

                      <Separator />
                      <p className="text-xs sm:text-sm font-medium text-muted-foreground">Dados Pessoais</p>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                        <div>
                          <Label className="text-xs sm:text-sm">CPF</Label>
                          <Input value={formData.cpf} onChange={(e) => setFormData({ ...formData, cpf: e.target.value })} placeholder="000.000.000-00" className="text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs sm:text-sm">RG</Label>
                          <Input value={formData.rg} onChange={(e) => setFormData({ ...formData, rg: e.target.value })} className="text-sm" />
                        </div>
                        <div>
                          <Label>Órgão Exp.</Label>
                          <Input value={formData.rg_orgao} onChange={(e) => setFormData({ ...formData, rg_orgao: e.target.value })} placeholder="SSP/SP" />
                        </div>
                        <div>
                          <Label>Data Exp.</Label>
                          <Input type="date" value={formData.rg_data_exp} onChange={(e) => setFormData({ ...formData, rg_data_exp: e.target.value })} />
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label>Data Nascimento</Label>
                          <Input type="date" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} />
                        </div>
                        <div>
                          <Label>Gênero</Label>
                          <Select value={formData.gender || undefined} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {genderOptions.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Estado Civil</Label>
                          <Select value={formData.civil_status} onValueChange={(v) => setFormData({ ...formData, civil_status: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {civilStatusOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Cidade Natal</Label>
                          <Input value={formData.birth_city} onChange={(e) => setFormData({ ...formData, birth_city: e.target.value })} />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Nacionalidade</Label>
                          <Input value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} />
                        </div>
                        <div>
                          <Label>Profissão/Formação</Label>
                          <Input value={formData.profession} onChange={(e) => setFormData({ ...formData, profession: e.target.value })} />
                        </div>
                        <div>
                          <Label>Empresa/Local de Trabalho</Label>
                          <Input value={formData.workplace} onChange={(e) => setFormData({ ...formData, workplace: e.target.value })} />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.is_foreigner} onCheckedChange={(c) => setFormData({ ...formData, is_foreigner: c })} />
                        <Label>Estrangeiro(a)</Label>
                      </div>
                    </TabsContent>
                    
                    {/* ABA: Endereço e Comunicação */}
                    <TabsContent value="endereco" className="space-y-4 mt-4">
                      <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Endereço
                      </p>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label>CEP</Label>
                          <Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} placeholder="00000-000" />
                        </div>
                        <div className="col-span-2">
                          <Label>Rua/Avenida</Label>
                          <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                        </div>
                        <div>
                          <Label>Número</Label>
                          <Input value={formData.address_number} onChange={(e) => setFormData({ ...formData, address_number: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label>Complemento</Label>
                          <Input value={formData.complement} onChange={(e) => setFormData({ ...formData, complement: e.target.value })} placeholder="Apto, Bloco..." />
                        </div>
                        <div>
                          <Label>Bairro</Label>
                          <Input value={formData.neighborhood} onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })} />
                        </div>
                        <div>
                          <Label>Cidade</Label>
                          <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                        </div>
                        <div>
                          <Label>UF</Label>
                          <Select value={formData.state || undefined} onValueChange={(v) => setFormData({ ...formData, state: v })}>
                            <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                            <SelectContent>
                              {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Separator />
                      <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Comunicação
                      </p>

                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label>Telefone</Label>
                          <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(00) 0000-0000" />
                        </div>
                        <div>
                          <Label>Tel. Comercial</Label>
                          <Input value={formData.phone_commercial} onChange={(e) => setFormData({ ...formData, phone_commercial: e.target.value })} />
                        </div>
                        <div>
                          <Label>Celular *</Label>
                          <Input value={formData.cellphone} onChange={(e) => setFormData({ ...formData, cellphone: e.target.value })} placeholder="(00) 00000-0000" />
                        </div>
                        <div>
                          <Label>Operadora</Label>
                          <Select value={formData.cellphone_carrier || undefined} onValueChange={(v) => setFormData({ ...formData, cellphone_carrier: v })}>
                            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Vivo">Vivo</SelectItem>
                              <SelectItem value="Claro">Claro</SelectItem>
                              <SelectItem value="Tim">Tim</SelectItem>
                              <SelectItem value="Oi">Oi</SelectItem>
                              <SelectItem value="Outra">Outra</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-green-500" /> WhatsApp
                          </Label>
                          <Input value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} placeholder="(00) 00000-0000" />
                        </div>
                        <div className="col-span-2">
                          <Label className="flex items-center gap-2">
                            <Mail className="w-4 h-4" /> Email
                          </Label>
                          <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                        </div>
                      </div>

                      <div className="flex gap-6">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={formData.no_email} onCheckedChange={(c) => setFormData({ ...formData, no_email: c })} />
                          <Label className="text-sm">Não quero receber email</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox checked={formData.no_sms} onCheckedChange={(c) => setFormData({ ...formData, no_sms: c })} />
                          <Label className="text-sm">Não quero receber SMS</Label>
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* ABA: Responsáveis */}
                    <TabsContent value="responsaveis" className="space-y-4 mt-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-muted-foreground">Vincular Responsáveis</p>
                        <Button type="button" variant="outline" size="sm" onClick={openNewResponsibleFromStudent}>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Cadastrar Novo Responsável
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        {/* Responsável Financeiro */}
                        <div className="p-4 border rounded-lg space-y-3">
                          <Label className="text-base font-medium">Responsável Financeiro *</Label>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              placeholder="Digite o nome ou CPF para buscar..."
                              value={searchRespFinanceiro}
                              onChange={(e) => {
                                setSearchRespFinanceiro(e.target.value);
                                setShowRespFinanceiroList(true);
                              }}
                              onFocus={() => setShowRespFinanceiroList(true)}
                              className="pl-10"
                            />
                            {showRespFinanceiroList && searchRespFinanceiro.length >= 2 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {filterResponsibles(searchRespFinanceiro).length > 0 ? (
                                  filterResponsibles(searchRespFinanceiro).map(r => (
                                    <div
                                      key={r.id}
                                      onClick={() => {
                                        setFormData({ ...formData, responsible_id: r.id });
                                        setSearchRespFinanceiro(`${r.name} - ${formatCPF(r.cpf)}`);
                                        setShowRespFinanceiroList(false);
                                      }}
                                      className="p-3 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                                    >
                                      <p className="font-medium">{r.name}</p>
                                      <p className="text-xs text-gray-500">{formatCPF(r.cpf)}</p>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-3 text-gray-500 text-sm text-center">
                                    Nenhum responsável encontrado
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {formData.responsible_id && (
                            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                              <User className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-green-700 font-medium">
                                {responsibles.find(r => r.id === formData.responsible_id)?.name || 'Selecionado'}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="ml-auto h-6 w-6 p-0"
                                onClick={() => {
                                  setFormData({ ...formData, responsible_id: '' });
                                  setSearchRespFinanceiro('');
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Este responsável receberá as cobranças e comunicações financeiras
                          </p>
                        </div>

                        {/* Responsável Financeiro Opcional */}
                        <div className="p-4 border rounded-lg space-y-3">
                          <Label>Responsável Financeiro (Opcional)</Label>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              placeholder="Digite o nome ou CPF para buscar..."
                              value={searchRespOpcional}
                              onChange={(e) => {
                                setSearchRespOpcional(e.target.value);
                                setShowRespOpcionalList(true);
                              }}
                              onFocus={() => setShowRespOpcionalList(true)}
                              className="pl-10"
                            />
                            {showRespOpcionalList && searchRespOpcional.length >= 2 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {filterResponsibles(searchRespOpcional).length > 0 ? (
                                  filterResponsibles(searchRespOpcional).map(r => (
                                    <div
                                      key={r.id}
                                      onClick={() => {
                                        setFormData({ ...formData, responsible_financial_optional_id: r.id });
                                        setSearchRespOpcional(`${r.name} - ${formatCPF(r.cpf)}`);
                                        setShowRespOpcionalList(false);
                                      }}
                                      className="p-3 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                                    >
                                      <p className="font-medium">{r.name}</p>
                                      <p className="text-xs text-gray-500">{formatCPF(r.cpf)}</p>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-3 text-gray-500 text-sm text-center">
                                    Nenhum responsável encontrado
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {formData.responsible_financial_optional_id && (
                            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-blue-700 font-medium">
                                {responsibles.find(r => r.id === formData.responsible_financial_optional_id)?.name || 'Selecionado'}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="ml-auto h-6 w-6 p-0"
                                onClick={() => {
                                  setFormData({ ...formData, responsible_financial_optional_id: '' });
                                  setSearchRespOpcional('');
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Responsável Pedagógico/Didático */}
                        <div className="p-4 border rounded-lg space-y-3">
                          <Label>Responsável Pedagógico/Didático</Label>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              placeholder="Digite o nome ou CPF para buscar (ou deixe vazio para usar o financeiro)..."
                              value={searchRespDidatico}
                              onChange={(e) => {
                                setSearchRespDidatico(e.target.value);
                                setShowRespDidaticoList(true);
                              }}
                              onFocus={() => setShowRespDidaticoList(true)}
                              className="pl-10"
                            />
                            {showRespDidaticoList && searchRespDidatico.length >= 2 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {filterResponsibles(searchRespDidatico).length > 0 ? (
                                  filterResponsibles(searchRespDidatico).map(r => (
                                    <div
                                      key={r.id}
                                      onClick={() => {
                                        setFormData({ ...formData, responsible_didactic_id: r.id });
                                        setSearchRespDidatico(`${r.name} - ${formatCPF(r.cpf)}`);
                                        setShowRespDidaticoList(false);
                                      }}
                                      className="p-3 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                                    >
                                      <p className="font-medium">{r.name}</p>
                                      <p className="text-xs text-gray-500">{formatCPF(r.cpf)}</p>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-3 text-gray-500 text-sm text-center">
                                    Nenhum responsável encontrado
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {formData.responsible_didactic_id && (
                            <div className="flex items-center gap-2 p-2 bg-orange-50 rounded-lg">
                              <User className="w-4 h-4 text-orange-600" />
                              <span className="text-sm text-orange-700 font-medium">
                                {responsibles.find(r => r.id === formData.responsible_didactic_id)?.name || 'Selecionado'}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="ml-auto h-6 w-6 p-0"
                                onClick={() => {
                                  setFormData({ ...formData, responsible_didactic_id: '' });
                                  setSearchRespDidatico('');
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Se não selecionar, será usado o responsável financeiro para comunicações pedagógicas
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* ABA: Saúde */}
                    <TabsContent value="saude" className="space-y-4 mt-4">
                      <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Heart className="w-4 h-4" /> Informações de Saúde
                      </p>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Tipo Sanguíneo</Label>
                          <Select value={formData.blood_type || undefined} onValueChange={(v) => setFormData({ ...formData, blood_type: v })}>
                            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {bloodTypes.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Cor/Raça</Label>
                          <Select value={formData.skin_color || undefined} onValueChange={(v) => setFormData({ ...formData, skin_color: v })}>
                            <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {skinColors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Deficiências</Label>
                          <Input value={formData.disabilities} onChange={(e) => setFormData({ ...formData, disabilities: e.target.value })} placeholder="Se houver, descreva" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Alergias</Label>
                        <Textarea value={formData.allergies} onChange={(e) => setFormData({ ...formData, allergies: e.target.value })} placeholder="Descreva alergias conhecidas..." rows={2} />
                      </div>

                      <div className="space-y-2">
                        <Label>Doenças / Condições Médicas</Label>
                        <Textarea value={formData.diseases} onChange={(e) => setFormData({ ...formData, diseases: e.target.value })} placeholder="Descreva doenças ou condições médicas..." rows={2} />
                      </div>

                      <div className="space-y-2">
                        <Label>Orientações de Primeiros Socorros</Label>
                        <Textarea value={formData.first_aid_notes} onChange={(e) => setFormData({ ...formData, first_aid_notes: e.target.value })} placeholder="Instruções em caso de emergência..." rows={2} />
                      </div>
                    </TabsContent>
                    
                    {/* ABA: Financeiro */}
                    <TabsContent value="financeiro" className="space-y-4 mt-4">
                      {!formData.class_name ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Selecione uma turma na aba "Dados" para ver os planos disponíveis</p>
                        </div>
                      ) : !selectedClassGroup?.course_id ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>A turma selecionada não está vinculada a um curso</p>
                          <p className="text-sm">Vincule a turma a um curso na página de Turmas</p>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-medium">Selecione os planos financeiros para este aluno:</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Object.entries(PLAN_CATEGORIES).map(([catKey, catInfo]) => {
                              const plans = getAvailablePlansForCategory(catKey);
                              const Icon = catInfo.icon;
                              const isSelected = !!selectedPlans[catKey];
                              
                              return (
                                <Card key={catKey} className={`border ${isSelected ? 'border-primary bg-primary/5' : 'border-border/60'}`}>
                                  <CardHeader className="pb-2">
                                    <div className="flex items-center gap-2">
                                      <Icon className="w-5 h-5" />
                                      <CardTitle className="text-base">{catInfo.label}</CardTitle>
                                    </div>
                                  </CardHeader>
                                  <CardContent className="space-y-2">
                                    {plans.length === 0 ? (
                                      <p className="text-sm text-muted-foreground">Nenhum plano disponível</p>
                                    ) : (
                                      <>
                                        <Select 
                                          value={selectedPlans[catKey] || 'none'} 
                                          onValueChange={(v) => setSelectedPlans({
                                            ...selectedPlans, 
                                            [catKey]: v === 'none' ? undefined : v
                                          })}
                                        >
                                          <SelectTrigger>
                                            <SelectValue placeholder="Não incluir" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">Não incluir</SelectItem>
                                            {plans.map(p => (
                                              <SelectItem key={p.id} value={p.id}>
                                                {p.name} - {p.num_installments}x R$ {p.installment_value?.toFixed(2)}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        {selectedPlans[catKey] && (
                                          <div className="space-y-2 pt-2 border-t">
                                            <div className="text-xs text-muted-foreground">
                                              {(() => {
                                                const plan = plans.find(p => p.id === selectedPlans[catKey]);
                                                if (!plan) return null;
                                                const total = plan.num_installments * plan.installment_value;
                                                return `Total: R$ ${total.toFixed(2)} em ${plan.num_installments}x`;
                                              })()}
                                            </div>
                                            <div className="flex gap-2">
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-7 px-2 text-xs flex-1"
                                                onClick={() => {
                                                  const plan = plans.find(p => p.id === selectedPlans[catKey]);
                                                  if (plan) handleEditPlan(plan);
                                                }}
                                              >
                                                <Edit className="w-3 h-3 mr-1" />
                                                Editar Valor
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="destructive"
                                                size="sm"
                                                className="h-7 px-2 text-xs"
                                                onClick={() => handleRemovePlanFromCategory(catKey)}
                                              >
                                                <X className="w-3 h-3 mr-1" />
                                                Remover
                                              </Button>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                          
                          {/* Resumo */}
                          {Object.values(selectedPlans).filter(Boolean).length > 0 && (
                            <Card className="bg-primary/5 border-primary">
                              <CardContent className="pt-4">
                                <p className="font-medium mb-2">Resumo dos Planos Selecionados:</p>
                                <div className="space-y-1 text-sm">
                                  {Object.entries(selectedPlans).map(([catKey, planId]) => {
                                    if (!planId) return null;
                                    const plan = coursePlans.find(p => p.id === planId);
                                    if (!plan) return null;
                                    const catInfo = PLAN_CATEGORIES[catKey];
                                    const total = plan.num_installments * plan.installment_value;
                                    return (
                                      <div key={catKey} className="flex justify-between">
                                        <span>{catInfo?.label}:</span>
                                        <span className="font-medium">{plan.num_installments}x R$ {plan.installment_value?.toFixed(2)} = R$ {total.toFixed(2)}</span>
                                      </div>
                                    );
                                  })}
                                  <Separator className="my-2" />
                                  <div className="flex justify-between font-bold">
                                    <span>Total Geral:</span>
                                    <span>
                                      R$ {Object.values(selectedPlans)
                                        .filter(Boolean)
                                        .reduce((acc, planId) => {
                                          const plan = coursePlans.find(p => p.id === planId);
                                          return acc + (plan ? plan.num_installments * plan.installment_value : 0);
                                        }, 0).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </>
                      )}
                    </TabsContent>
                    
                    {/* ABA: Parcelas Mês a Mês */}
                    <TabsContent value="parcelas" className="space-y-4 mt-4">
                      {!editingStudent ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Salve o aluno primeiro para gerenciar as parcelas</p>
                        </div>
                      ) : parcelasLoading ? (
                        <div className="text-center py-8 text-muted-foreground">
                          Carregando parcelas...
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Label>Ano:</Label>
                              <Select value={parcelasAno.toString()} onValueChange={(v) => {
                                setParcelasAno(parseInt(v));
                                // Passar os planos selecionados ao mudar o ano
                                const selectedPlanIds = Object.values(selectedPlans).filter(Boolean);
                                loadParcelasAluno(editingStudent.id, selectedPlanIds.length > 0 ? selectedPlanIds : editingStudent.financial_plans);
                              }}>
                                <SelectTrigger className="w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {[2024, 2025, 2026, 2027].map(ano => (
                                    <SelectItem key={ano} value={ano.toString()}>{ano}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {canEdit() && (
                              <Button onClick={handleGerarTodasParcelas} size="sm" variant="outline">
                                <Plus className="w-4 h-4 mr-1" />
                                Gerar Todas
                              </Button>
                            )}
                          </div>

                          <div className="border rounded-lg overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="p-2 text-left font-medium">Mês</th>
                                  <th className="p-2 text-left font-medium">Descrição</th>
                                  <th className="p-2 text-left font-medium">Valor (R$)</th>
                                  <th className="p-2 text-left font-medium">Desconto</th>
                                  <th className="p-2 text-left font-medium">Final</th>
                                  <th className="p-2 text-left font-medium">Vencimento</th>
                                  <th className="p-2 text-left font-medium">Status</th>
                                  <th className="p-2 text-left font-medium">Pago</th>
                                  {canEdit() && <th className="p-2 text-center font-medium w-20">Ações</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {parcelas.map((parcela, index) => (
                                  <tr key={parcela.mes} className={parcela.status === 'pago' ? 'bg-green-50' : ''}>
                                    <td className="p-2 font-medium">{parcela.mesNome}</td>
                                    <td className="p-2 text-xs text-muted-foreground max-w-[150px] truncate" title={parcela.descricao}>
                                      {parcela.descricao || 'Mensalidade'}
                                    </td>
                                    <td className="p-2">
                                      {canEdit() && parcela.status !== 'pago' ? (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={parcela.valor_original}
                                          onChange={(e) => handleParcelaChange(index, 'valor_original', e.target.value)}
                                          className="w-24 h-8"
                                        />
                                      ) : (
                                        `R$ ${(parcela.valor_original || 0).toFixed(2)}`
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {canEdit() && parcela.status !== 'pago' ? (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={parcela.desconto}
                                          onChange={(e) => handleParcelaChange(index, 'desconto', e.target.value)}
                                          className="w-24 h-8"
                                        />
                                      ) : (
                                        <span className="text-green-600">-R$ {(parcela.desconto || 0).toFixed(2)}</span>
                                      )}
                                    </td>
                                    <td className="p-2 font-medium">R$ {(parcela.valor_final || 0).toFixed(2)}</td>
                                    <td className="p-2">
                                      {canEdit() && parcela.status !== 'pago' ? (
                                        <Input
                                          type="date"
                                          value={parcela.vencimento?.split('T')[0] || ''}
                                          onChange={(e) => handleParcelaChange(index, 'vencimento', e.target.value)}
                                          className="w-32 h-8"
                                        />
                                      ) : (
                                        parcela.vencimento ? new Date(parcela.vencimento).toLocaleDateString('pt-BR') : '-'
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {parcela.status === 'pago' ? (
                                        <Badge className="bg-green-500 text-white">Pago</Badge>
                                      ) : parcela.status === 'pendente' ? (
                                        <Badge className="bg-amber-500 text-white">Pendente</Badge>
                                      ) : parcela.status === 'vencido' ? (
                                        <Badge className="bg-red-500 text-white">Vencido</Badge>
                                      ) : (
                                        <Badge variant="outline">Não gerado</Badge>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {parcela.status === 'pago' ? (
                                        <span className="font-bold text-green-600">
                                          R$ {(parcela.valor_pago || parcela.valor_final || 0).toFixed(2)}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    {canEdit() && (
                                      <td className="p-2 text-center">
                                        <div className="flex gap-1 justify-center">
                                          {parcela.status !== 'pago' && (
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 w-7 p-0"
                                              onClick={() => handleSaveParcelaIndividual(parcela, index)}
                                              title={parcela.existente ? "Atualizar" : "Criar"}
                                            >
                                              <CreditCard className="w-4 h-4" />
                                            </Button>
                                          )}
                                          {parcela.existente && parcela.status !== 'pago' && (
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 w-7 p-0 text-red-600"
                                              onClick={() => handleDeleteParcela(parcela, index)}
                                              title="Excluir"
                                            >
                                              <X className="w-4 h-4" />
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Totais */}
                          <Card className="bg-blue-50 border-blue-200">
                            <CardContent className="pt-4">
                              <div className="grid grid-cols-4 gap-4 text-center text-sm">
                                <div>
                                  <p className="text-muted-foreground">Total Bruto</p>
                                  <p className="font-bold">
                                    R$ {parcelas.reduce((sum, p) => sum + (parseFloat(p.valor_original) || 0), 0).toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Descontos</p>
                                  <p className="font-bold text-green-600">
                                    -R$ {parcelas.reduce((sum, p) => sum + (parseFloat(p.desconto) || 0), 0).toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">A Pagar</p>
                                  <p className="font-bold text-blue-600">
                                    R$ {parcelas.reduce((sum, p) => sum + (parseFloat(p.valor_final) || 0), 0).toFixed(2)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Pago</p>
                                  <p className="font-bold text-green-600">
                                    R$ {parcelas.filter(p => p.status === 'pago').reduce((sum, p) => sum + (parseFloat(p.valor_pago) || parseFloat(p.valor_final) || 0), 0).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </>
                      )}
                    </TabsContent>
                    
                    {/* ABA: Outros */}
                    <TabsContent value="outros" className="space-y-4 mt-4">
                      <p className="text-sm font-medium text-muted-foreground">Autorização de Retirada</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Nome de quem pode retirar</Label>
                          <Input value={formData.authorized_pickup} onChange={(e) => setFormData({ ...formData, authorized_pickup: e.target.value })} />
                        </div>
                        <div>
                          <Label>Telefone</Label>
                          <Input value={formData.authorized_pickup_phone} onChange={(e) => setFormData({ ...formData, authorized_pickup_phone: e.target.value })} />
                        </div>
                        <div>
                          <Label>Grau de Parentesco</Label>
                          <Input value={formData.authorized_pickup_relation} onChange={(e) => setFormData({ ...formData, authorized_pickup_relation: e.target.value })} />
                        </div>
                      </div>

                      <Separator />
                      <p className="text-sm font-medium text-muted-foreground">Permanência</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Horário de Entrada</Label>
                          <Input type="time" value={formData.permanence_entry} onChange={(e) => setFormData({ ...formData, permanence_entry: e.target.value })} />
                        </div>
                        <div>
                          <Label>Horário de Saída</Label>
                          <Input type="time" value={formData.permanence_exit} onChange={(e) => setFormData({ ...formData, permanence_exit: e.target.value })} />
                        </div>
                      </div>

                      <Separator />
                      <div className="space-y-2">
                        <Label>Observações Gerais</Label>
                        <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Outras informações importantes..." rows={4} />
                      </div>
                    </TabsContent>
                  </Tabs>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" data-testid="save-student">
                      {editingStudent ? 'Salvar Alterações' : 'Criar Aluno'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Dialog Responsável (separado para funcionar tanto sozinho quanto dentro do aluno) */}
      <Dialog open={respDialogOpen} onOpenChange={(open) => {
        setRespDialogOpen(open);
        if (!open) resetResponsibleForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              {editingResponsible ? 'Editar Responsável' : 'Novo Responsável'}
            </DialogTitle>
            <DialogDescription>
              {newResponsibleFromStudent 
                ? 'Cadastre o responsável que será vinculado ao aluno'
                : 'Preencha os dados do responsável financeiro'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateResponsible} className="space-y-4">
            <Tabs defaultValue="dados" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="dados">Dados Pessoais</TabsTrigger>
                <TabsTrigger value="contato">Contato</TabsTrigger>
                <TabsTrigger value="endereco">Endereço</TabsTrigger>
              </TabsList>
              
              <TabsContent value="dados" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nome Completo *</Label>
                    <Input value={respFormData.name} onChange={(e) => setRespFormData({ ...respFormData, name: e.target.value })} required data-testid="resp-name" />
                  </div>
                  <div>
                    <Label>CPF *</Label>
                    <Input value={respFormData.cpf} onChange={(e) => setRespFormData({ ...respFormData, cpf: e.target.value })} placeholder="000.000.000-00" required data-testid="resp-cpf" />
                  </div>
                  <div>
                    <Label>RG</Label>
                    <Input value={respFormData.rg} onChange={(e) => setRespFormData({ ...respFormData, rg: e.target.value })} />
                  </div>
                  <div>
                    <Label>Órgão Emissor</Label>
                    <Input value={respFormData.rg_orgao} onChange={(e) => setRespFormData({ ...respFormData, rg_orgao: e.target.value })} placeholder="SSP/SP" />
                  </div>
                  <div>
                    <Label>Data de Nascimento</Label>
                    <Input type="date" value={respFormData.birth_date} onChange={(e) => setRespFormData({ ...respFormData, birth_date: e.target.value })} />
                  </div>
                  <div>
                    <Label>Nacionalidade</Label>
                    <Input value={respFormData.nationality} onChange={(e) => setRespFormData({ ...respFormData, nationality: e.target.value })} />
                  </div>
                  <div>
                    <Label>Profissão</Label>
                    <Input value={respFormData.profession} onChange={(e) => setRespFormData({ ...respFormData, profession: e.target.value })} />
                  </div>
                  <div>
                    <Label>Local de Trabalho</Label>
                    <Input value={respFormData.workplace} onChange={(e) => setRespFormData({ ...respFormData, workplace: e.target.value })} />
                  </div>
                  <div>
                    <Label>Grau de Parentesco</Label>
                    <Select value={respFormData.relation_type} onValueChange={(v) => setRespFormData({ ...respFormData, relation_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {relationTypes.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Textarea value={respFormData.notes} onChange={(e) => setRespFormData({ ...respFormData, notes: e.target.value })} rows={2} />
                </div>
              </TabsContent>
              
              <TabsContent value="contato" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Telefone Fixo</Label>
                    <Input value={respFormData.phone} onChange={(e) => setRespFormData({ ...respFormData, phone: e.target.value })} placeholder="(00) 0000-0000" />
                  </div>
                  <div>
                    <Label>Tel. Comercial</Label>
                    <Input value={respFormData.phone_commercial} onChange={(e) => setRespFormData({ ...respFormData, phone_commercial: e.target.value })} />
                  </div>
                  <div>
                    <Label>Celular *</Label>
                    <Input value={respFormData.cellphone} onChange={(e) => setRespFormData({ ...respFormData, cellphone: e.target.value })} placeholder="(00) 00000-0000" data-testid="resp-cellphone" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-green-500" /> WhatsApp
                    </Label>
                    <Input value={respFormData.whatsapp} onChange={(e) => setRespFormData({ ...respFormData, whatsapp: e.target.value })} placeholder="(00) 00000-0000" data-testid="resp-whatsapp" />
                    <p className="text-xs text-muted-foreground mt-1">Número para cobrança via WhatsApp</p>
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email Principal
                    </Label>
                    <Input type="email" value={respFormData.email} onChange={(e) => setRespFormData({ ...respFormData, email: e.target.value })} data-testid="resp-email" />
                  </div>
                  <div>
                    <Label>Email Secundário</Label>
                    <Input type="email" value={respFormData.email_secondary} onChange={(e) => setRespFormData({ ...respFormData, email_secondary: e.target.value })} />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="endereco" className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>CEP</Label>
                    <Input value={respFormData.zip_code} onChange={(e) => setRespFormData({ ...respFormData, zip_code: e.target.value })} placeholder="00000-000" />
                  </div>
                  <div className="col-span-2">
                    <Label>Rua/Avenida</Label>
                    <Input value={respFormData.address} onChange={(e) => setRespFormData({ ...respFormData, address: e.target.value })} />
                  </div>
                  <div>
                    <Label>Número</Label>
                    <Input value={respFormData.address_number} onChange={(e) => setRespFormData({ ...respFormData, address_number: e.target.value })} />
                  </div>
                  <div>
                    <Label>Complemento</Label>
                    <Input value={respFormData.complement} onChange={(e) => setRespFormData({ ...respFormData, complement: e.target.value })} />
                  </div>
                  <div>
                    <Label>Bairro</Label>
                    <Input value={respFormData.neighborhood} onChange={(e) => setRespFormData({ ...respFormData, neighborhood: e.target.value })} />
                  </div>
                  <div>
                    <Label>Cidade</Label>
                    <Input value={respFormData.city} onChange={(e) => setRespFormData({ ...respFormData, city: e.target.value })} />
                  </div>
                  <div>
                    <Label>UF</Label>
                    <Select value={respFormData.state} onValueChange={(v) => setRespFormData({ ...respFormData, state: v })}>
                      <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                      <SelectContent>
                        {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRespDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-responsible">
                {editingResponsible ? 'Salvar Alterações' : 'Criar Responsável'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input className="pl-10" placeholder="Buscar por nome, matrícula ou turma..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" />
      </div>

      {/* Lista de Alunos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredStudents.map((student) => {
          const responsible = responsibles.find(r => r.id === student.responsible_id);
          const otherResponsibles = getStudentResponsibles(student, student.responsible_id);
          
          return (
            <Card key={student.id} className="border-border/60 hover:border-primary/30 hover:shadow-md transition-all" data-testid="student-card">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{student.name}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      {student.registration_number} • {student.class_name}
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      student.situation === 'Ativo' || student.active !== false
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {student.situation || (student.active !== false ? 'Ativo' : 'Inativo')}
                    </span>
                    {/* Botão Plano Financeiro */}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50" 
                      onClick={() => openFinancialPlan(student)}
                      title="Ver Plano Financeiro"
                      data-testid={`financial-plan-${student.id}`}
                    >
                      <CreditCard className="w-4 h-4" />
                    </Button>
                    {canEdit() && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50" 
                          onClick={() => openHistoryDialog(student)}
                          title="Histórico de Transferências"
                          data-testid={`history-student-${student.id}`}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 text-orange-600 hover:text-orange-700 hover:bg-orange-50" 
                          onClick={() => openTransferDialog(student)}
                          title="Transferir de Turma"
                          data-testid={`transfer-student-${student.id}`}
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEditStudent(student)} data-testid={`edit-student-${student.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {/* Responsável Financeiro */}
                {responsible && (
                  <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="text-xs text-blue-600 font-medium">Responsável Financeiro</p>
                          <p className="font-medium text-sm">{responsible.name}</p>
                        </div>
                      </div>
                      {canEdit() && (
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEditResponsible(responsible)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {responsible.whatsapp && (
                        <button
                          onClick={() => openWhatsApp(responsible.whatsapp, responsible.name)}
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded-full transition-colors"
                          title="Abrir WhatsApp"
                        >
                          <MessageCircle className="w-3 h-3" />
                          {formatPhone(responsible.whatsapp)}
                        </button>
                      )}
                      {responsible.cellphone && !responsible.whatsapp && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          <Phone className="w-3 h-3" />
                          {formatPhone(responsible.cellphone)}
                        </span>
                      )}
                      {responsible.email && (
                        <a 
                          href={`mailto:${responsible.email}`}
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded-full transition-colors truncate max-w-[200px]"
                        >
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{responsible.email}</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Outros Responsáveis (Pedagógico, Pai/Mãe que não são o responsável financeiro) */}
                {otherResponsibles.length > 0 && (
                  <div className="pt-2 border-t space-y-2">
                    {otherResponsibles.map((resp) => (
                      <div key={resp.id} className="p-2 bg-purple-50 rounded-lg border border-purple-100 text-xs">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-purple-600" />
                            <div>
                              <p className="text-xs text-purple-600 font-medium">
                                {resp.role || resp.relation_type || 'Responsável'}
                              </p>
                              <p className="font-medium text-sm">{resp.name}</p>
                            </div>
                          </div>
                          {canEdit() && (
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => handleEditResponsible(resp)}>
                              <Edit className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {resp.whatsapp && (
                            <button
                              onClick={() => openWhatsApp(resp.whatsapp, resp.name)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500 hover:bg-green-600 text-white text-xs rounded-full transition-colors"
                              title="Abrir WhatsApp"
                            >
                              <MessageCircle className="w-2.5 h-2.5" />
                              {formatPhone(resp.whatsapp)}
                            </button>
                          )}
                          {resp.cellphone && !resp.whatsapp && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                              <Phone className="w-2.5 h-2.5" />
                              {formatPhone(resp.cellphone)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredStudents.length === 0 && (
        <div className="text-center py-12">
          <GraduationCap className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Nenhum aluno encontrado</p>
          {canEdit() && (
            <Button className="mt-4" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Primeiro Aluno
            </Button>
          )}
        </div>
      )}

      {/* Dialog de Edição de Plano Financeiro */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Editar Plano Financeiro
            </DialogTitle>
            <DialogDescription>
              Configure os valores e descontos do plano
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Nome do Plano</Label>
              <Input
                value={planFormData.name}
                onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nº de Parcelas</Label>
                <Input
                  type="number"
                  min="1"
                  max="48"
                  value={planFormData.num_installments}
                  onChange={(e) => setPlanFormData({ ...planFormData, num_installments: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Valor da Parcela (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={planFormData.installment_value}
                  onChange={(e) => setPlanFormData({ ...planFormData, installment_value: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dia de Vencimento</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={planFormData.due_day}
                  onChange={(e) => setPlanFormData({ ...planFormData, due_day: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Data de Início</Label>
                <Input
                  type="date"
                  value={planFormData.start_date}
                  onChange={(e) => setPlanFormData({ ...planFormData, start_date: e.target.value })}
                />
              </div>
            </div>
            
            {/* Seção de Descontos */}
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Desconto por Antecipação</Label>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="discount_enabled"
                    checked={planFormData.discount_enabled}
                    onCheckedChange={(checked) => setPlanFormData({ ...planFormData, discount_enabled: checked })}
                  />
                  <label htmlFor="discount_enabled" className="text-sm cursor-pointer">
                    Ativar desconto
                  </label>
                </div>
              </div>
              
              {planFormData.discount_enabled && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Desconto (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={planFormData.discount_percent}
                        onChange={(e) => setPlanFormData({ ...planFormData, discount_percent: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Dias antes do vencimento</Label>
                      <Input
                        type="number"
                        min="1"
                        max="30"
                        value={planFormData.discount_days_before}
                        onChange={(e) => setPlanFormData({ ...planFormData, discount_days_before: e.target.value })}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-blue-700">
                    {planFormData.discount_percent}% de desconto se pago até {planFormData.discount_days_before} dias antes do vencimento
                  </p>
                  <p className="text-sm font-medium text-blue-800">
                    Valor com desconto: R$ {(parseFloat(planFormData.installment_value || 0) * (1 - parseFloat(planFormData.discount_percent || 0) / 100)).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            
            {/* Preview do total */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800">
                Total: R$ {(parseFloat(planFormData.installment_value || 0) * parseInt(planFormData.num_installments || 1)).toFixed(2)}
              </p>
              <p className="text-xs text-green-600">
                {planFormData.num_installments}x de R$ {parseFloat(planFormData.installment_value || 0).toFixed(2)}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePlan}>
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Transferência de Turma */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-orange-600" />
              Transferir Aluno de Turma
            </DialogTitle>
            <DialogDescription>
              {transferringStudent && (
                <>
                  Transferir <strong>{transferringStudent.name}</strong> da turma <strong>{transferringStudent.class_name}</strong> para outra turma.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Turma de Destino */}
            <div className="space-y-2">
              <Label htmlFor="transfer-class">Nova Turma *</Label>
              <Select 
                value={transferFormData.new_class_group_id} 
                onValueChange={(v) => setTransferFormData({ ...transferFormData, new_class_group_id: v })}
              >
                <SelectTrigger id="transfer-class">
                  <SelectValue placeholder="Selecione a turma de destino" />
                </SelectTrigger>
                <SelectContent>
                  {classGroups
                    .filter(cg => cg.id !== transferringStudent?.class_group_id)
                    .map(cg => (
                      <SelectItem key={cg.id} value={cg.id}>
                        {cg.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Número de Chamada na Nova Turma */}
            <div className="space-y-2">
              <Label htmlFor="transfer-call-number">Nº de Chamada na Nova Turma (opcional)</Label>
              <Input
                id="transfer-call-number"
                type="number"
                min="1"
                placeholder="Deixe vazio para gerar depois"
                value={transferFormData.new_call_number}
                onChange={(e) => setTransferFormData({ ...transferFormData, new_call_number: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Se não definir agora, use "Gerar Números (A-Z)" na página de Relatórios
              </p>
            </div>
            
            {/* Motivo da Transferência */}
            <div className="space-y-2">
              <Label htmlFor="transfer-reason">Motivo da Transferência (opcional)</Label>
              <Textarea
                id="transfer-reason"
                placeholder="Ex: Pedido da família, adaptação pedagógica..."
                value={transferFormData.reason || ''}
                onChange={(e) => setTransferFormData({ ...transferFormData, reason: e.target.value })}
                rows={2}
              />
            </div>
            
            {/* Opções de Histórico */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
              <p className="text-sm font-medium text-blue-800">Transferir histórico:</p>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="keep-grades"
                  checked={transferFormData.keep_grades}
                  onCheckedChange={(c) => setTransferFormData({ ...transferFormData, keep_grades: c })}
                />
                <Label htmlFor="keep-grades" className="text-sm cursor-pointer">
                  Manter notas e boletim
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="keep-attendance"
                  checked={transferFormData.keep_attendance}
                  onCheckedChange={(c) => setTransferFormData({ ...transferFormData, keep_attendance: c })}
                />
                <Label htmlFor="keep-attendance" className="text-sm cursor-pointer">
                  Manter registros de presença/falta
                </Label>
              </div>
            </div>
            
            {/* Aviso */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800">
                <strong>Atenção:</strong> O histórico do aluno será associado à nova turma. Esta ação não pode ser desfeita automaticamente.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)} disabled={transferLoading}>
              Cancelar
            </Button>
            <Button 
              onClick={handleTransferStudent} 
              disabled={transferLoading || !transferFormData.new_class_group_id}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {transferLoading ? 'Transferindo...' : 'Confirmar Transferência'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Histórico de Transferências */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Histórico de Transferências
            </DialogTitle>
            <DialogDescription>
              {historyStudent && (
                <>
                  Histórico de mudanças de turma de <strong>{historyStudent.name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {historyLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-muted-foreground mt-2">Carregando histórico...</p>
              </div>
            ) : transferHistory.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <History className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Nenhuma transferência registrada</p>
                <p className="text-xs text-muted-foreground mt-1">
                  O aluno está na turma original desde a matrícula
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Info atual */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium mb-1">Turma Atual</p>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{historyStudent?.class_name || '-'}</span>
                    {historyStudent?.call_number && (
                      <Badge variant="outline" className="bg-white">
                        Nº {historyStudent.call_number}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Lista de transferências */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Transferências ({transferHistory.length})</p>
                  {transferHistory.map((transfer, index) => (
                    <div 
                      key={transfer.id || index} 
                      className="p-3 bg-white border rounded-lg shadow-sm"
                    >
                      {/* De -> Para */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">De:</p>
                          <p className="text-sm font-medium">{transfer.from_class_name || 'N/A'}</p>
                          {transfer.from_call_number && (
                            <Badge variant="secondary" className="text-xs mt-1">
                              Nº {transfer.from_call_number}
                            </Badge>
                          )}
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">Para:</p>
                          <p className="text-sm font-medium">{transfer.to_class_name || 'N/A'}</p>
                          {transfer.to_call_number && (
                            <Badge variant="secondary" className="text-xs mt-1">
                              Nº {transfer.to_call_number}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Data e responsável */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground border-t pt-2 mt-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatTransferDate(transfer.transfer_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {transfer.transferred_by_name || transfer.transferred_by || 'Sistema'}
                        </span>
                      </div>
                      
                      {/* Motivo */}
                      {transfer.reason && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                          <span className="font-medium">Motivo: </span>
                          {transfer.reason}
                        </div>
                      )}
                      
                      {/* O que foi transferido */}
                      <div className="flex gap-2 mt-2">
                        {transfer.grades_transferred && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            Notas transferidas
                          </Badge>
                        )}
                        {transfer.attendance_transferred && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                            Faltas transferidas
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
